import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Check, Copy, Share2 } from "lucide-react";
import { toast } from "sonner";
import WaitlistSignupForm, { WaitlistFormData } from "./WaitlistSignupForm";
import { useWaitlist, WaitlistSignupResult } from "@/hooks/useWaitlist";

interface WaitlistModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const WaitlistModal = ({ open, onOpenChange }: WaitlistModalProps) => {
  const [searchParams] = useSearchParams();
  const [successData, setSuccessData] = useState<WaitlistSignupResult | null>(null);
  const [copied, setCopied] = useState(false);
  const { submitToWaitlist, isSubmitting } = useWaitlist();

  // Get referral code from URL if present
  const referredByCode = searchParams.get("ref") || sessionStorage.getItem("waitlist_ref") || undefined;

  // Store ref code in session storage for persistence
  useEffect(() => {
    const refParam = searchParams.get("ref");
    if (refParam) {
      sessionStorage.setItem("waitlist_ref", refParam);
    }
  }, [searchParams]);

  const handleSubmit = async (data: WaitlistFormData) => {
    try {
    const result = await submitToWaitlist({
      email: data.email,
      first_name: data.first_name,
      last_name: data.last_name,
      zip_code: data.zip_code,
        experience_level: data.experience_level,
        vehicle_type: data.vehicle_type,
        features_of_interest: data.features_of_interest,
        referral_source: data.referral_source,
        referred_by_code: referredByCode,
      });
      setSuccessData(result);
      toast.success("You're on the list!");
    } catch (error) {
      // Error handling is done in the hook
    }
  };

  const referralLink = successData
    ? `${window.location.origin}?ref=${successData.referral_code}`
    : "";

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      toast.success("Link copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error("Failed to copy link");
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Join the Airing Down Waitlist",
          text: "Check out Airing Down - where off-road adventures are mapped!",
          url: referralLink,
        });
      } catch (e) {
        // User cancelled or share failed
      }
    } else {
      handleCopyLink();
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    // Reset success state after modal animation completes
    setTimeout(() => setSuccessData(null), 300);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        {!successData ? (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl">Join the Waitlist</DialogTitle>
              <DialogDescription>
                Be among the first to explore when we launch. Limited early access spots available.
              </DialogDescription>
            </DialogHeader>
            <WaitlistSignupForm
              onSubmit={handleSubmit}
              isSubmitting={isSubmitting}
              referredByCode={referredByCode}
            />
          </>
        ) : (
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-primary" />
            </div>
            
            <DialogTitle className="text-2xl mb-2">You're In!</DialogTitle>
            
            <p className="text-muted-foreground mb-6">
              We'll send you an email when early access becomes available.
            </p>

            <div className="space-y-4">
              <p className="text-sm font-medium">Know someone who'd love this?</p>
              
              <div className="flex gap-2">
                <Input
                  value={referralLink}
                  readOnly
                  className="text-sm"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleCopyLink}
                  className="shrink-0"
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-primary" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>

              <Button
                variant="outline"
                className="w-full"
                onClick={handleShare}
              >
                <Share2 className="mr-2 h-4 w-4" />
                Share with Friends
              </Button>
            </div>

            <Button
              variant="ghost"
              className="mt-6"
              onClick={handleClose}
            >
              Close
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default WaitlistModal;
